export const DELETE_WINDOW_MS = 5 * 60 * 1000; // 5 minutes
export const MAX_MESSAGE_FILE_SIZE_BYTES = 5 * 1024 * 1024;

const toDateSafe = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") {
    try {
      return value.toDate();
    } catch (err) {
      return null;
    }
  }
  const d = new Date(value);
  // Use Number.isNaN to avoid global isNaN coercion
  return Number.isNaN(d.getTime()) ? null : d;
};

export const getCreatedTime = (message) => {
  return toDateSafe(message?.createdAt);
};

export const canDeleteForAll = (message, actorType) => {
  if (!message) return false;
  if (actorType === "company" && message?.sender !== "company") return false;
  if (actorType !== "company" && message?.sender === "company") return false;

  const created = getCreatedTime(message);
  if (!created) return false;

  const elapsed = Date.now() - created.getTime();
  const withinWindow = elapsed <= DELETE_WINDOW_MS;

  const otherHasRead =
    actorType === "company" ? !!message?.readByParticipant : !!message?.readByCompany;

  // Allow delete-for-all if we're still within the window OR the other party hasn't read yet
  return withinWindow || !otherHasRead;
};

export const statusLabel = (message, actorType) => {
  if (!message) return "";
  if (message.deletedForCompany || message.deletedForParticipant) return "Deleted";

  const isMine = actorType === "company" ? message.sender === "company" : message.sender !== "company";
  if (!isMine) {
    const readByViewer = actorType === "company" ? message.readByCompany : message.readByParticipant;
    return readByViewer ? "Read" : "Received";
  }

  const read = actorType === "company" ? message.readByParticipant : message.readByCompany;
  return read ? "Read" : "Sent";
};

/**
 * Given a thread document id (company__participant) return the participant id for the
 * provided company. If the thread does not belong to the company, return null.
 */
export const getParticipantIdFromThread = (threadId, companyName) => {
  if (!threadId || !companyName) return null;
  const prefix = `${companyName}__`;
  if (!threadId.startsWith(prefix)) return null;
  const participantId = threadId.slice(prefix.length);
  return participantId || null;
};

/**
 * Build a stable Firestore document id for a company ↔ participant thread.
 * Non word characters are normalized so we can safely use it as a document id.
 */
export const buildThreadId = (companyName, participantId) => {
  if (!companyName || !participantId) return null;
  return `${companyName}__${participantId}`.replace(/[^\w-]+/g, "_");
};

export const buildResponderInitials = (value) => {
  if (!value) return "";
  const base = String(value).trim();
  if (!base) return "";

  const normalized = base.includes("@") ? base.split("@")[0] : base;
  const parts = normalized
    .split(/[\s._-]+/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (!parts.length) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
};

export const isMessageModerationAllowed = (result) => {
  if (!result || typeof result !== "object") return true;
  if (result.blockCompany === true || result.blocked === true || result.isAllowed === false) return false;

  const label = String(result.label || result.classification || "").toLowerCase();
  return label !== "negative" && label !== "inappropriate";
};
